import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MysteryScorePayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  bowlerId?: number | number[];
  bowlerName?: string | string[];
  sideEventId: number;
  sideEventType?: string;

  leagueId: number;
  defaultAmount: number;
  appliesTo: 'bowler' | 'team' | 'doubles';
  placement?: number;
  onPayoutComplete: (winnerId: number, amount: number) => void;
}

const MysteryScorePayoutModal: React.FC<MysteryScorePayoutModalProps> = ({
  isOpen,
  onClose,
  bowlerId,
  bowlerName,
  sideEventId,
  sideEventType,
  leagueId,
  defaultAmount,
  appliesTo,
  placement,
  onPayoutComplete,
}) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [payoutAmount, setPayoutAmount] = useState(defaultAmount.toString());
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  if (!isOpen) return null;

  const handleRecordPayout = async () => {
    if (!bowlerId || isProcessing) return;

    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      alert(t('payoutModals.pleaseEnterValidAmount'));
      return;
    }

    setIsProcessing(true);

    try {
      // For doubles, bowlerId is an array; for others, it's a single number
      if (appliesTo === 'doubles' && Array.isArray(bowlerId)) {
        for (let i = 0; i < bowlerId.length; i++) {
          const id = bowlerId[i];
          if (typeof id !== 'number' || isNaN(id)) {
            console.error('Invalid bowlerId for payout:', id);
            continue;
          }
          
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api'}/admin/payouts/mysteryscore`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
              },
              body: JSON.stringify({
                sideEventId,
                leagueId,
                recipientType: appliesTo === 'doubles' ? 'bowler' : appliesTo,
                recipientId: id, 
                amount,
                sideEventType: 'side_event',
                placement: placement || 1,
                notes: notes.trim(),
              }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to record payout');
          }

          onPayoutComplete(id, amount);
        }
      } else {
        // Single payout (individual or team)
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api'}/admin/payouts/mysteryscore`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({
              sideEventId,
              leagueId,
              recipientType: appliesTo,
              recipientId: bowlerId,
              amount,
              sideEventType: 'side_event',
              placement: placement || 1,
              notes: notes.trim(),
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to record payout');
        }

        onPayoutComplete(bowlerId as number, amount);
      }

      setIsPaid(true);
      setShowConfirmation(true);

      // Auto-hide confirmation after 4 seconds
      setTimeout(() => setShowConfirmation(false), 4000);
    } catch (error) {
      console.error('Error recording payout:', error);
      alert(
        t('payoutModals.payoutFailed', {
          error: error instanceof Error ? error.message : 'Unknown error'
        })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;

    onClose();
    setNotes('');
    setPayoutAmount(defaultAmount.toString());
    setIsPaid(false);
    setShowConfirmation(false);
  };

   // Helper to render winner info for all types
  const renderWinnerInfo = () => {
    if (appliesTo === 'doubles' && Array.isArray(bowlerId) && Array.isArray(bowlerName)) {
      return (
        <div className="flex flex-col gap-4">
          {bowlerId.map((id, idx) => (
            <div key={id} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                #{idx + 1}
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-800">
                  {bowlerName[idx]}
                </h4>
                <div className="text-sm text-gray-600">{t('payoutModals.id')}: {id}</div>
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
            #1
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-800">
              {bowlerName}
            </h4>
            <div className="text-sm text-gray-600">{t('payoutModals.id')}: {bowlerId}</div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{t('payoutModals.mysteryScorePayout')}</h2>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Payout Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('payoutModals.payoutAmountCanBeModified')}
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('payoutModals.enterPayoutAmount')}
              />
            </div>
          </div>

          {/* Notes Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('payoutModals.notesOptional')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('payoutModals.scoreTypeGameSquadDetails')}
            />
          </div>

          {/* Winner Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {t('payoutModals.mysteryScoreWinner')} {bowlerName
                ? Array.isArray(bowlerName)
                  ? ` (${bowlerName.join(' & ')})`
                  : ` (${bowlerName})`
                : ''}
            </h3>

            <div
               className={`border rounded-lg p-4 transition-all duration-300 ${isPaid
                  ? 'bg-green-50 border-green-200 opacity-60'
                  : 'bg-gray-50 border-gray-200'
                }`}
            >
              {showConfirmation && (
                <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-green-800 font-medium">
                      {t('payoutModals.payoutRecordedFor', {
                        name: bowlerName
                          ? Array.isArray(bowlerName)
                            ? bowlerName.join(' & ')
                            : bowlerName
                          : ''
                      })}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {renderWinnerInfo()}
                </div>

                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm text-gray-500">{t('payoutModals.payoutAmount')}</div>
                    <div className="text-lg font-bold text-green-600">
                      ${parseFloat(payoutAmount).toFixed(2)}
                    </div>
                  </div>

                  {!isPaid ? (
                    <button
                      onClick={handleRecordPayout}
                      disabled={
                        isProcessing ||
                        parseFloat(payoutAmount) <= 0 ||
                        isNaN(parseFloat(payoutAmount))
                      }
                      className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition duration-200 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? t('payoutModals.recording') : t('manageSideEvent.processPayout')}
                    </button>
                  ) : (
                    <div className="bg-green-500 text-white px-4 py-2 rounded-lg">
                      ✓ {t('manageSideEvent.paid')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Modal Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 disabled:opacity-50"
            >
              {t('manageSideEvent.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MysteryScorePayoutModal;
